/* La Sura - the builder's Step by step layout (loop 4, DECISIONS 2026-09-27).

   Presentation only: it moves nothing and changes no form field. The blocks
   in the product details column stay where they are, so every other script
   (variant picker, builder, preview, extras, summary) works unchanged; this
   shows one step at a time and hides the rest with a class. Not the hidden
   attribute: the builder and extras treat [hidden] as "filtered out", and a
   choice on another step must still post and be charged.

   Which blocks make which step, and the other helpers, are in
   lasura-cake-steps-parts.js (loaded first). Blocks before this one (title,
   price, preview) stay in view throughout.

   Continue checks the step's required fields with the browser's own
   validation; on the last step it adds to the basket. A single-choice pick
   by pointer moves on by itself, never by keyboard nor on a priced step. Browser back moves
   between steps. An add refused for a choice elsewhere opens that step
   first. Without JavaScript it's the scroll page. */
class LasuraCakeSteps extends HTMLElement {
  connectedCallback() {
    try {
      this.cfg = JSON.parse(this.querySelector('[data-steps-config]').textContent);
    } catch {
      return;
    }
    this.p = window.LasuraCakeStepsParts;
    if (!this.p) return;
    this.column = this.parentElement;
    this.steps = this.p.collect(this, this.cfg);
    if (this.steps.length < 2) return;
    this.all = this.steps.flatMap((step) => step.els);
    this.column.classList.add('lasura-steps-on');
    this.ui = this.querySelector('[data-steps-ui]');
    this.ui.hidden = false;
    this.walker = this.ui.querySelector('[data-steps-walk]') || this.ui;
    this.rail = window.LasuraCakeStepsRail?.build(this);
    this.nav = this.querySelector('[data-steps-nav]');
    this.column.appendChild(this.nav);
    /* The one-page link, under the step rather than between it and the cake. */
    const link = this.querySelector('.lasura-steps__switch');
    if (link) this.column.insertBefore(link, this.nav);
    this.nav.hidden = false;
    this.nav.addEventListener('click', (event) => {
      const act = event.target.closest('[data-steps-act]')?.dataset.stepsAct;
      if (act === 'back') this.go(this.index - 1, -1);
      if (act === 'next') this.next();
    });
    /* On the document: the bar can sit outside the column (beside the cake). */
    document.addEventListener('pointerdown', () => (this.pointerAt = performance.now()), true);
    this.column.addEventListener('change', (event) => this.maybeAdvance(event));
    /* Skip becomes Continue as soon as something optional is picked or typed
       (after the other listeners, which may open a follow-up field). */
    ['change', 'input'].forEach((type) => document.addEventListener(type, () => setTimeout(() => this.nextLabel(), 0)));
    /* The last step's price lands after the summary reads it. */
    document.addEventListener('lasura:cake-summary', () => setTimeout(() => this.nextLabel(), 0));
    window.addEventListener('popstate', () => this.go(this.fromHash(), 0, false));
    window.addEventListener('invalid', (event) => this.onInvalid(event), true);
    this.p.mirrorTotal(this.column, this.nav);
    /* A choice listed beside the cake opens its step
       (assets/lasura-cake-choices.js), which then brings the choice into view. */
    document.addEventListener('lasura:cake-open', (event) => {
      const at = this.steps.findIndex((step) => this.p.holds(step, event.detail.target));
      if (at >= 0 && at !== this.index) this.go(at, at < this.index ? -1 : 1, true, false);
    });
    this.go(this.fromHash(), 0, false, false);
    /* The preview may carry the bar into its column beside the steps. */
    document.dispatchEvent(new CustomEvent('lasura:steps-ready'));
    this.p.track('builder_start');
  }

  slug(i) {
    return `step-${i + 1}`;
  }

  fromHash() {
    const n = Number((location.hash.match(/^#step-(\d+)$/) || [])[1]);
    return n >= 1 && n <= this.steps.length ? n - 1 : 0;
  }

  go(i, dir = 0, push = true, focus = true) {
    const index = Math.max(0, Math.min(this.steps.length - 1, i));
    const step = this.steps[index];
    this.all.forEach((el) => el.classList.toggle('lasura-step-off', !step.els.includes(el)));
    const shell = this.steps.shell;
    if (shell) shell.els.forEach((el) => el.classList.toggle('lasura-step-off', !step.els.some((s) => shell.builder.contains(s))));
    if (dir) {
      step.els.forEach((el) => {
        el.classList.remove('lasura-step-in', 'lasura-step-back');
        void el.offsetWidth;
        el.classList.add(dir > 0 ? 'lasura-step-in' : 'lasura-step-back');
      });
    }
    this.index = index;
    /* One question per step: which of the picker's options shows (CSS in the block). */
    if (step.part) this.column.dataset.lasuraPart = step.part;
    else delete this.column.dataset.lasuraPart;
    const total = this.steps.length;
    const label = this.ui.querySelector('[data-steps-label]');
    const fill = (text) => (text || '').split('{n}').join(index + 1).split('{total}').join(total).split('{title}').join(step.title || '');
    const full = fill(this.cfg.progress);
    this.p.heading(label, step, full, fill(this.cfg.count));
    /* The named stages, if set, decide where Sura stands (lasura-cake-steps-rail.js). */
    this.furthest = Math.max(this.furthest || 0, index);
    const done = this.rail ? window.LasuraCakeStepsRail.show(this.rail, index, this.furthest) : (index + 1) / total;
    document.dispatchEvent(new CustomEvent('lasura:cake-step', { detail: { label: full, title: step.title || '', n: index + 1, total } }));
    /* On the bar's wrapper, which Sura shares and which may sit on the cake. */
    const walker = this.walker;
    walker.style.setProperty('--lasura-steps-done', done.toFixed(3));
    if (dir) {
      walker.classList.toggle('is-back', dir < 0);
      walker.classList.remove('is-walking');
      void walker.offsetWidth;
      walker.classList.add('is-walking');
      clearTimeout(this.walk);
      this.walk = setTimeout(() => walker.classList.remove('is-walking'), 650);
    }
    this.nav.querySelector('[data-steps-act="back"]').hidden = index === 0;
    this.nav.classList.toggle('is-review', !!step.review);
    this.nextLabel();
    if (push && location.hash !== `#${this.slug(index)}`) history.pushState(null, '', `#${this.slug(index)}`);
    if (focus) {
      this.bring();
      /* The heading takes focus for screen readers; its ring shows only when
         the step was changed from the keyboard. */
      label.toggleAttribute('data-by-pointer', performance.now() - (this.pointerAt || 0) < 1500);
      label.focus({ preventScroll: true });
    }
    if (dir) this.p.track('builder_step', { step: index + 1, name: step.title || '' });
  }

  /* The heading to its place (lasura-cake-steps-parts.js); the builder
     uses it for a missing choice too. */
  bring() {
    this.p.bring(this);
  }

  /* Continue, or Skip on an optional step with nothing picked; beside the
     cake on desktop, the long form names the next step (CSS in the block). */
  nextLabel() {
    const step = this.steps[this.index];
    if (!step) return;
    const button = this.nav.querySelector('[data-steps-act="next"]'), short = this.nav.querySelector('[data-steps-next-label]');
    /* Last step: Continue's place holds the page's own add (or Save changes,
       or an enquiry), its price dropped if it won't fit. */
    const real = step.review && this.p.addTarget();
    button.disabled = !!(real && real.disabled);
    if (step.review) {
      short.textContent = real ? real.textContent.replace(/\s+/g, ' ').trim() : this.cfg.next;
      const l = this.nav.querySelector('[data-steps-next-long]');
      if (l) l.textContent = '';
      if (short.scrollWidth > button.clientWidth - 16) short.textContent = short.textContent.split(' · ')[0];
      return;
    }
    const skip = this.p.optionalEmpty(step), next = this.steps[this.index + 1];
    short.textContent = skip ? this.cfg.skip : this.cfg.next;
    const long = this.nav.querySelector('[data-steps-next-long]');
    if (!long) return;
    long.textContent = next ? ((skip ? this.cfg.skipTo : this.cfg.nextTo) || '').split('{title}').join(next.title || '') : '';
    /* Too long for the button's fixed width: the short label, not a cut one. */
    if (long.offsetWidth && long.scrollWidth > long.clientWidth + 1) long.textContent = '';
  }

  /* checkValidity fires the builder's invalid handler, which shows the
     group's message and moves focus to the first choice. */
  next() {
    const step = this.steps[this.index];
    if (step.review) return this.p.addTarget()?.click();
    const invalid = this.p.fields(step).filter((field) => !field.checkValidity());
    if (invalid.length) return;
    this.go(this.index + 1, 1);
  }

  maybeAdvance(event) {
    const step = this.steps[this.index];
    const input = event.target;
    if (step.review || input.type !== 'radio' || performance.now() - (this.pointerAt || 0) > 900 || this.p.priced?.(step)) return;
    /* Checked when the timer runs too: the pick may have opened a follow-up
       field ("Which number?"), and then the shopper has more to do here. */
    const single = () => new Set(this.p.fields(step).map((f) => (f.type === 'radio' ? f.name : f))).size === 1;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.steps[this.index] === step && single() && this.next(), matchMedia('(prefers-reduced-motion: reduce)').matches ? 150 : 450);
  }

  /* The add was refused for a field on another step: open that step first,
     before the builder moves focus to it. */
  onInvalid(event) {
    const at = this.steps.findIndex((step) => this.p.holds(step, event.target));
    if (at >= 0 && at !== this.index) this.go(at, at < this.index ? -1 : 1, true, false);
  }
}

if (!customElements.get('lasura-cake-steps')) customElements.define('lasura-cake-steps', LasuraCakeSteps);
